import "./index.less";
import React, {
  ReactElement,
  ReactNode,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

type FieldKey = string | number | symbol;
type MaybeFieldKey = FieldKey | null | undefined | false;
type FormValues = Record<FieldKey, any>;
type FormListener = () => void;
type FormOnValuesChange = (changedValues: FormValues, allValues: FormValues) => void;

export interface FormError {
  message?: ReactNode;
}

export interface FormRule {
  required?: boolean;
  message?: ReactNode;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  type?: string;
  validator?: (
    value: any,
    callback: (error?: ReactNode) => void
  ) => void | ReactNode | Promise<void | ReactNode>;
  [key: string]: any;
}

export interface FormInstance<
  Values extends FormValues = FormValues,
  _FieldData = any,
  FieldName extends FieldKey = keyof Values & FieldKey
> {
  clearFields: (fields?: MaybeFieldKey | MaybeFieldKey[]) => void;
  getFieldValue: (field: FieldName) => any;
  getFields: () => Values;
  getFieldsValue: () => Values;
  resetFields: (fields?: MaybeFieldKey | MaybeFieldKey[]) => void;
  setFieldValue: (field: FieldName, value: any) => void;
  setFields: (
    fields: Partial<Record<FieldName, { error?: FormError | null }>>
  ) => void;
  setFieldsValue: (values: Partial<Values>) => void;
  validate: (fields?: MaybeFieldKey[]) => Promise<Partial<Values>>;
  __INTERNAL__: FormStore;
}

export interface FormProps
  extends Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit"> {
  colon?: boolean;
  disabled?: boolean;
  form?: FormInstance;
  initialValues?: FormValues;
  layout?: "horizontal" | "vertical" | "inline" | string;
  onSubmit?: (values: FormValues, event?: React.FormEvent<HTMLFormElement>) => void;
  onValuesChange?: FormOnValuesChange;
  size?: "mini" | "small" | "default" | "large" | string;
  [key: string]: any;
}

export interface FormItemProps {
  children?: ReactNode | ((values: FormValues) => ReactNode);
  className?: string;
  disabled?: boolean;
  field?: FieldKey;
  initialValue?: any;
  label?: ReactNode;
  labelAlign?: "left" | "right" | string;
  normalize?: (value: any, previousValue: any, allValues: FormValues) => any;
  onBlur?: (...args: any[]) => void;
  onFocus?: (...args: any[]) => void;
  required?: boolean;
  rules?: FormRule[];
  shouldUpdate?: boolean | ((prev: FormValues, next: FormValues) => boolean);
  style?: React.CSSProperties;
  validateTrigger?: string | string[];
  [key: string]: any;
}

interface FieldMeta {
  initialValue?: any;
  required?: boolean;
  rules?: FormRule[];
}

interface FormContextValue {
  disabled?: boolean;
  form: FormInstance;
}

interface FormStore {
  clearFields: (fields?: MaybeFieldKey | MaybeFieldKey[]) => void;
  getError: (field: FieldKey) => FormError | undefined;
  getFieldValue: (field: FieldKey) => any;
  getFields: () => FormValues;
  getFieldsValue: () => FormValues;
  registerField: (field: FieldKey, meta: FieldMeta) => () => void;
  resetFields: (fields?: MaybeFieldKey | MaybeFieldKey[]) => void;
  setCallbacks: (callbacks: { onValuesChange?: FormOnValuesChange }) => void;
  setFieldValue: (field: FieldKey, value: any) => void;
  setFields: (fields: Record<FieldKey, { error?: FormError | null }>) => void;
  setFieldsValue: (values: Partial<FormValues>) => void;
  setInitialValues: (values?: FormValues) => void;
  subscribe: (listener: FormListener) => () => void;
  validate: (fields?: MaybeFieldKey[]) => Promise<FormValues>;
}

const FormContext = createContext<FormContextValue | null>(null);

const normalizeFieldList = (
  fields?: MaybeFieldKey | MaybeFieldKey[]
): FieldKey[] | undefined => {
  if (fields === undefined) {
    return undefined;
  }

  const list = Array.isArray(fields) ? fields : [fields];
  return list.filter((field): field is FieldKey => Boolean(field));
};

const cloneValues = (values: FormValues): FormValues => ({ ...values });

const isEmptyValue = (value: any) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const getRuleMessage = (rule: FormRule, fallback: string) =>
  rule.message ?? fallback;

const validateRule = async (
  value: any,
  rule: FormRule
): Promise<ReactNode | undefined> => {
  if (rule.required && isEmptyValue(value)) {
    return getRuleMessage(rule, "");
  }

  if (value !== undefined && value !== null && value !== "") {
    const valueLength = String(value).length;

    if (rule.minLength !== undefined && valueLength < rule.minLength) {
      return getRuleMessage(rule, "");
    }

    if (rule.maxLength !== undefined && valueLength > rule.maxLength) {
      return getRuleMessage(rule, "");
    }

    if (rule.min !== undefined && Number(value) < rule.min) {
      return getRuleMessage(rule, "");
    }

    if (rule.max !== undefined && Number(value) > rule.max) {
      return getRuleMessage(rule, "");
    }

    if (rule.type === "email" && typeof value === "string") {
      const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!emailLike) {
        return getRuleMessage(rule, "");
      }
    }
  }

  if (!rule.validator) {
    return undefined;
  }

  let callbackError: ReactNode | undefined;
  let callbackCalled = false;
  const callback = (error?: ReactNode) => {
    callbackCalled = true;
    callbackError = error;
  };

  const result = await rule.validator(value, callback);

  if (callbackCalled) {
    return callbackError;
  }

  if (result === undefined) {
    return undefined;
  }

  return result as ReactNode;
};

const getValueFromChangeArgs = (args: any[]) => {
  const first = args[0];

  if (first?.target) {
    if ("checked" in first.target && first.target.type === "checkbox") {
      return first.target.checked;
    }

    return first.target.value;
  }

  if (first?.currentTarget) {
    return first.currentTarget.value;
  }

  return first;
};

const composeHandler =
  (...handlers: Array<((...args: any[]) => void) | undefined>) =>
  (...args: any[]) => {
    handlers.forEach((handler) => handler?.(...args));
  };

const createFormInstance = (): FormInstance => {
  let values: FormValues = {};
  let initialValues: FormValues = {};
  let errors: Partial<Record<FieldKey, FormError>> = {};
  const fields = new Map<FieldKey, FieldMeta>();
  let callbacks: { onValuesChange?: FormOnValuesChange } = {};
  const listeners = new Set<FormListener>();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const emitValuesChange = (changedValues: FormValues) => {
    if (Object.keys(changedValues).length === 0) {
      return;
    }

    callbacks.onValuesChange?.(cloneValues(changedValues), cloneValues(values));
  };

  const setValue = (
    field: FieldKey,
    value: any,
    options: { emit?: boolean } = {}
  ) => {
    values = {
      ...values,
      [field]: value,
    };
    errors = {
      ...errors,
      [field]: undefined,
    };

    if (options.emit) {
      emitValuesChange({ [field]: value });
    }

    notify();
  };

  const validateField = async (field: FieldKey) => {
    const meta = fields.get(field);
    const fieldRules = [
      ...(meta?.required ? [{ required: true, message: "" }] : []),
      ...(meta?.rules || []),
    ];
    const value = values[field];

    for (const rule of fieldRules) {
      const message = await validateRule(value, rule);

      if (message !== undefined) {
        return { message };
      }
    }

    return undefined;
  };

  const store: FormStore = {
    clearFields: (fieldList) => {
      const normalized = normalizeFieldList(fieldList);

      if (!normalized) {
        errors = {};
      } else {
        const nextErrors = { ...errors };
        normalized.forEach((field) => {
          delete nextErrors[field];
        });
        errors = nextErrors;
      }

      notify();
    },
    getError: (field) => errors[field],
    getFieldValue: (field) => values[field],
    getFields: () => cloneValues(values),
    getFieldsValue: () => cloneValues(values),
    registerField: (field, meta) => {
      fields.set(field, meta);

      if (meta.initialValue !== undefined) {
        initialValues = {
          ...initialValues,
          [field]: meta.initialValue,
        };

        if (values[field] === undefined) {
          values = {
            ...values,
            [field]: meta.initialValue,
          };
          notify();
        }
      }

      return () => {
        fields.delete(field);
      };
    },
    resetFields: (fieldList) => {
      const normalized = normalizeFieldList(fieldList);

      if (!normalized) {
        values = cloneValues(initialValues);
        errors = {};
        notify();
        return;
      }

      const nextValues = { ...values };
      const nextErrors = { ...errors };

      normalized.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(initialValues, field)) {
          nextValues[field] = initialValues[field];
        } else {
          delete nextValues[field];
        }
        delete nextErrors[field];
      });

      values = nextValues;
      errors = nextErrors;
      notify();
    },
    setCallbacks: (nextCallbacks) => {
      callbacks = nextCallbacks;
    },
    setFieldValue: (field, value) => setValue(field, value, { emit: true }),
    setFields: (nextFields) => {
      const nextErrors = { ...errors };

      Object.entries(nextFields).forEach(([field, config]) => {
        if (config?.error) {
          nextErrors[field] = config.error;
        } else {
          delete nextErrors[field];
        }
      });

      errors = nextErrors;
      notify();
    },
    setFieldsValue: (nextValues) => {
      const changedValues: FormValues = {};
      const nextErrors = { ...errors };

      Object.entries(nextValues).forEach(([field, value]) => {
        values = {
          ...values,
          [field]: value,
        };
        changedValues[field] = value;
        delete nextErrors[field];
      });

      errors = nextErrors;
      emitValuesChange(changedValues);
      notify();
    },
    setInitialValues: (nextInitialValues = {}) => {
      initialValues = {
        ...initialValues,
        ...nextInitialValues,
      };

      const nextValues = { ...values };
      let changed = false;

      Object.entries(nextInitialValues).forEach(([field, value]) => {
        if (nextValues[field] === undefined) {
          nextValues[field] = value;
          changed = true;
        }
      });

      if (changed) {
        values = nextValues;
        notify();
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    validate: async (fieldList) => {
      const normalized = normalizeFieldList(fieldList);
      const targetFields = normalized ?? Array.from(fields.keys());
      const nextErrors = { ...errors };
      const validationErrors: Record<FieldKey, FormError> = {};

      for (const field of targetFields) {
        const error = await validateField(field);

        if (error) {
          nextErrors[field] = error;
          validationErrors[field] = error;
        } else {
          delete nextErrors[field];
        }
      }

      errors = nextErrors;
      notify();

      if (Object.keys(validationErrors).length > 0) {
        return Promise.reject(validationErrors);
      }

      return normalized
        ? normalized.reduce<FormValues>((result, field) => {
            result[field] = values[field];
            return result;
          }, {})
        : cloneValues(values);
    },
  };

  const instance: FormInstance = {
    clearFields: store.clearFields,
    getFieldValue: store.getFieldValue,
    getFields: store.getFields,
    getFieldsValue: store.getFieldsValue,
    resetFields: store.resetFields,
    setFieldValue: store.setFieldValue,
    setFields: store.setFields,
    setFieldsValue: store.setFieldsValue,
    validate: store.validate,
    __INTERNAL__: store,
  };

  return instance;
};

const useForm = <Values extends FormValues = FormValues>(): [
  FormInstance<Values>
] => {
  const formRef = useRef<FormInstance<Values>>();

  if (!formRef.current) {
    formRef.current = createFormInstance() as FormInstance<Values>;
  }

  return [formRef.current];
};

const useFormUpdate = (form?: FormInstance | null) => {
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!form) {
      return undefined;
    }

    return form.__INTERNAL__.subscribe(() => {
      setVersion((version) => version + 1);
    });
  }, [form]);
};

const FormRoot = (props: FormProps) => {
  const {
    children,
    className,
    colon: _colon,
    disabled,
    form,
    initialValues,
    layout = "horizontal",
    onSubmit,
    onValuesChange,
    size,
    ...rest
  } = props;
  const [internalForm] = useForm();
  const currentForm = form || internalForm;

  useFormUpdate(currentForm);

  useEffect(() => {
    currentForm.__INTERNAL__.setCallbacks({ onValuesChange });
  }, [currentForm, onValuesChange]);

  useEffect(() => {
    currentForm.__INTERNAL__.setInitialValues(initialValues);
  }, [currentForm, initialValues]);

  const contextValue = useMemo<FormContextValue>(
    () => ({
      disabled,
      form: currentForm,
    }),
    [currentForm, disabled]
  );

  return (
    <FormContext.Provider value={contextValue}>
      <form
        {...rest}
        className={classNames(
          "form-ui",
          `form-ui-layout-${layout}`,
          size ? `form-ui-size-${size}` : undefined,
          {
            "form-ui-disabled": disabled,
          },
          className
        )}
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.(currentForm.getFieldsValue(), event);
        }}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
};

const renderLabel = (label: ReactNode, labelAlign?: FormItemProps["labelAlign"]) => {
  if (label === undefined || label === null) {
    return null;
  }

  return (
    <div
      className={classNames(
        "form-ui-label-item",
        "form-ui-item-label-col",
        labelAlign ? `form-ui-label-align-${labelAlign}` : undefined
      )}
    >
      <label>{label}</label>
    </div>
  );
};

const FormItem = (props: FormItemProps) => {
  const {
    children,
    className,
    disabled,
    field,
    initialValue,
    label,
    labelAlign,
    normalize,
    onBlur,
    onFocus,
    required,
    rules,
    shouldUpdate,
    style,
    validateTrigger: _validateTrigger,
    ...rest
  } = props;
  const context = useContext(FormContext);
  const form = context?.form;

  useFormUpdate(form);

  useEffect(() => {
    if (!form || field === undefined || field === null) {
      return undefined;
    }

    return form.__INTERNAL__.registerField(field, {
      initialValue,
      required,
      rules,
    });
  }, [field, form, initialValue, required, rules]);

  if (!context) {
    return (
      <div className={classNames("form-ui-item", className)} style={style}>
        {children as ReactNode}
      </div>
    );
  }

  const error = field === undefined || field === null
    ? undefined
    : form.__INTERNAL__.getError(field);
  const values = form.getFieldsValue();
  const currentValue = field === undefined || field === null
    ? undefined
    : form.getFieldValue(field);
  const itemDisabled = Boolean(context.disabled || disabled);
  const childContent =
    typeof children === "function" && shouldUpdate
      ? children(values)
      : children;
  const onlyChild = React.Children.count(childContent) === 1
    ? React.Children.only(childContent as ReactElement)
    : childContent;
  let controlNode = childContent as ReactNode;

  if (
    field !== undefined &&
    field !== null &&
    isValidElement(onlyChild)
  ) {
    const child = onlyChild as ReactElement<any>;
    const childProps = child.props || {};
    const handleChange = (...args: any[]) => {
      childProps.onChange?.(...args);
      const rawValue = getValueFromChangeArgs(args);
      const previousValue = form.getFieldValue(field);
      const normalizedValue = normalize
        ? normalize(rawValue, previousValue, form.getFieldsValue())
        : rawValue;

      form.setFieldValue(field, normalizedValue);
    };
    const injectedProps: Record<string, any> = {
      disabled: itemDisabled || childProps.disabled,
      onBlur: composeHandler(childProps.onBlur, onBlur),
      onChange: handleChange,
      onFocus: composeHandler(childProps.onFocus, onFocus),
      value: currentValue,
    };

    if (typeof child.type !== "string" && error) {
      injectedProps.status = childProps.status ?? "error";
      injectedProps.error = childProps.error ?? true;
    } else if (typeof child.type === "string" && error) {
      injectedProps["aria-invalid"] = true;
    }

    controlNode = React.cloneElement(child, injectedProps);
  }

  return (
    <div
      {...rest}
      className={classNames(
        "form-ui-item",
        {
          "form-ui-item-disabled": itemDisabled,
          "form-ui-item-error": error,
          "form-ui-item-status-error": error,
        },
        className
      )}
      style={style}
    >
      {renderLabel(label, labelAlign)}
      <div className="form-ui-item-wrapper">
        <div className="form-ui-item-control">
          <div className="form-ui-item-control-children">{controlNode}</div>
          {error ? (
            <div className="form-ui-item-message" role="alert">
              {error.message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

type FormComponent = React.FC<FormProps> & {
  Item: React.FC<FormItemProps>;
  useForm: typeof useForm;
};

const FormUI = FormRoot as FormComponent;

FormUI.Item = FormItem;
FormUI.useForm = useForm;

export const FormItemUI = FormItem;
export type { FormValues };

export default FormUI;
